import React, { useState, useEffect, useCallback } from "react";
import {
  TextInput,
  Textarea,
  Stack,
  Text,
  Grid,
  Alert,
  InputLabel,
  Title,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { DatePickerInput, TimeInput } from "@mantine/dates";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

import {
  EventTeam,
  EventTeamInput,
  Event,
  EventInput,
  omit,
  apiDateStringToDate,
  apiTimeToFormTime,
  isValidTimeFormat,
  dateToApiDateString,
  formTimeToApiTime,
  Room,
} from "@/utils";

import { AppIcon, AppModal } from "@/components/generic";

import { RoomSelector } from "../Rooms";
import { ClientSelector } from "../Clients/ClientSelector";
import { EventTeamSelector } from "../EventTeamSelector";
import { EventStatusSelector } from "../EventStatusSelector";
import { EventexternalUrlTypeSelector } from "../EventexternalUrlTypeSelector";
import { CategoriesInput } from "../Categories";
import { useCategoriesContext } from "@/contexts";

// Enable dayjs plugins
dayjs.extend(customParseFormat);

export interface EventFormModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Function to close the modal */
  onClose: (event?: Event) => void;

  /** Event to edit (if null/undefined, it's create mode) */
  event?: Event | null;

  /** Venue ID for new events */
  venueId?: string;

  /** Recurring event ID if creating an override or instance */
  recurringEventId?: string;

  /** Callback when event should be created */
  onCreateEvent?: (eventData: EventInput) => Promise<Event | null>;

  /** Callback when event should be updated */
  onUpdateEvent?: (
    eventId: string,
    eventData: EventInput,
  ) => Promise<Event | null>;

  /** Whether create operation is in progress */
  isCreating?: boolean;

  /** Whether update operation is in progress */
  isUpdating?: boolean;

  /** Last error from event operations */
  lastError?: string | null;

  /** Callback to clear errors */
  onClearError?: () => void;
}

interface EventFormData {
  name: string;
  recurringEventId?: string | null | undefined;
  description: string;
  eventDate: Date | null;
  startTime: string;
  endTime: string;
  color: string;
  categories?: string[] | null | undefined;
  imageUploadedFileId?: string | null | undefined;
  imageUrl?: string | null | undefined;
  externalUrlType?: string | null | undefined;
  externalUrl?: string | null | undefined;
  status: number;
  roomId: string;
  clientId: string;
  eventTeams: EventTeamInput[];
  rooms: Room[];
}

const initialFormData: EventFormData = {
  name: "",
  description: "",
  eventDate: null,
  startTime: "",
  endTime: "",
  color: "#3b82f6",
  categories: [],
  imageUrl: null,
  externalUrlType: null,
  externalUrl: null,
  imageUploadedFileId: null,
  status: 100,
  roomId: "",
  clientId: "",
  eventTeams: [],
  rooms: [],
};

export const EventFormModal: React.FC<EventFormModalProps> = ({
  opened,
  onClose,
  event,
  venueId,
  recurringEventId,
  onCreateEvent,
  onUpdateEvent,
  isCreating = false,
  isUpdating = false,
  lastError,
  onClearError,
}) => {
  const [formData, setFormData] = useState<EventFormData>(initialFormData);
  const [validationErrors, setValidationErrors] = useState<any>({});
  const { ensureCategories } = useCategoriesContext();

  const isEditMode = Boolean(event) && Boolean(event?.id);
  const isSubmitting = isCreating || isUpdating;
  const isRecurringEventInstance = Boolean(recurringEventId);

  const prepareOutTeam = useCallback((team: EventTeam): EventTeamInput => {
    return {
      ...omit(team as any, ["__typename", "id"]),
      supportingRooms:
        team.supportingRooms?.map((room) => room.id ?? "").filter(Boolean) ??
        [],
    };
  }, []);

  // Reset form when modal opens/closes or event changes
  useEffect(() => {
    if (opened) {
      if (event) {
        // Edit mode - populate form with event data
        setFormData({
          name: event.name || "",
          recurringEventId: event.recurringEventId || null,
          description: event.description || "",
          eventDate: apiDateStringToDate(event.eventDate),
          startTime: apiTimeToFormTime(event.startTime),
          endTime: apiTimeToFormTime(event.endTime),
          color: event.color || "#3b82f6",
          categories: event.categories?.map((category) => category.name ?? ""),
          imageUploadedFileId: event.imageUploadedFileId,
          imageUrl: event.imageUrl,
          externalUrlType: event.externalUrlType,
          externalUrl: event.externalUrl,
          status: event.status != null ? event.status : 100,
          roomId: event.roomId || "",
          clientId: event.clientId || "",
          eventTeams:
            event.eventTeams?.map((team) => prepareOutTeam(team)) || [],
          rooms: event.rooms ?? [],
        });
      } else {
        // Create mode - reset to initial values
        const newFormData = { ...initialFormData };
        // If creating from a recurring event, mark as override by default
        if (isRecurringEventInstance) {
          newFormData.recurringEventId = recurringEventId;
        }
        setFormData(newFormData);
      }
      setValidationErrors({});
      onClearError?.();
    }
  }, [opened, event, isRecurringEventInstance, onClearError, prepareOutTeam]);

  const validateForm = (): boolean => {
    const newErrors: any = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = "Event name is required";
    }

    if (!formData.eventDate) {
      newErrors.eventDate = "Event date is required";
    }

    if (!formData.startTime.trim()) {
      newErrors.startTime = "Start time is required";
    } else if (!isValidTimeFormat(formData.startTime)) {
      newErrors.startTime = "Please enter a valid time (e.g., 10:00 or 22:00)";
    }

    if (!formData.endTime.trim()) {
      newErrors.endTime = "End time is required";
    } else if (!isValidTimeFormat(formData.endTime)) {
      newErrors.endTime = "Please enter a valid time (e.g., 11:00 or 23:00)";
    }

    // Time validation
    // if (
    //   formData.startTime &&
    //   formData.endTime &&
    //   isValidTimeFormat(formData.startTime) &&
    //   isValidTimeFormat(formData.endTime) &&
    //   !compareTimeStrings(formData.startTime, formData.endTime)
    // ) {
    //   newErrors.endTime = "End time must be after start time";
    // }

    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof EventFormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev: any) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  // Handle client selection change
  const handleClientChange = (clientId: string | string[] | null) => {
    const selectedClientId =
      (Array.isArray(clientId) ? clientId[0] : clientId) || "";
    handleInputChange("clientId", selectedClientId);
  };

  // Handle main room selection change
  // const handleMainRoomChange = (roomId: string | string[] | null) => {
  //   const selectedRoomId = (Array.isArray(roomId) ? roomId[0] : roomId) || "";
  //   handleInputChange("roomId", selectedRoomId);
  // };

  const handleRoomsChange = (
    _: string | string[] | null,
    rooms?: Room | Room[],
  ) => {
    handleInputChange("rooms", rooms);
  };

  // Handle event teams change
  const handleEventTeamsChange = (eventTeams: EventTeamInput[]) => {
    handleInputChange("eventTeams", eventTeams);
  };

  // Get the error to display for a field
  const getDisplayError = (field: keyof EventFormData): string | undefined => {
    return validationErrors[field] as string;
  };

  const handleSubmit = async () => {
    // Clear errors on new submission
    onClearError?.();

    if (!validateForm()) {
      return;
    }

    try {
      // Prepare event data - convert Date and time back to API format
      const eventData: EventInput = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        eventDate: dateToApiDateString(formData.eventDate),
        startTime: formTimeToApiTime(formData.startTime),
        endTime: formTimeToApiTime(formData.endTime),
        color: formData.color,
        categories: formData.categories,
        imageUploadedFileId: formData.imageUploadedFileId,
        externalUrlType: formData.externalUrlType,
        externalUrl: formData.externalUrl,
        status: formData.status,
        venueId: venueId ?? "",
        recurringEventId: formData.recurringEventId ?? recurringEventId ?? null,
        roomId: formData.roomId || null,
        clientId: formData.clientId || null,
        eventTeams: formData.eventTeams,
        rooms: formData.rooms.map((room) => room.id || ""),
      };

      let result: Event | null = null;

      if (isEditMode && event && onUpdateEvent) {
        // Update existing event
        result = await onUpdateEvent(event.id, eventData);
      } else if (!isEditMode && onCreateEvent) {
        // Create new event
        result = await onCreateEvent(eventData);
      }

      // If successful, close the modal
      if (result) {
        ensureCategories(result.categories);
        onClose(result);
      }
    } catch (error) {
      // Error handling is managed by the hook/parent component
      console.error("Form submission error:", error);
    }
  };

  const handleCancel = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  // Render generic errors at the bottom of the form
  const renderGenericErrors = () => {
    if (!lastError) {
      return null;
    }

    return (
      <Alert
        color="red"
        icon={<AppIcon iconName="AlertCircle" size={16} />}
        title="Error"
      >
        <Text size="sm">{lastError}</Text>
      </Alert>
    );
  };

  // Get modal title based on context
  const getModalTitle = () => {
    if (isEditMode) {
      return "Edit Event";
    }
    if (isRecurringEventInstance) {
      return "Create Event Override";
    }
    return "Add New Event";
  };

  // Get modal description based on context
  const getEventTypeInfo = () => {
    if (isRecurringEventInstance && !isEditMode) {
      return (
        <Alert
          color="blue"
          mb="md"
          icon={<AppIcon iconName="InfoCircle" size={16} />}
        >
          <Text size="sm">
            You are creating an override for a recurring event. This will create
            a one-time event that takes precedence over the recurring pattern
            for this specific date.
          </Text>
        </Alert>
      );
    }
    return null;
  };

  return (
    <AppModal
      opened={opened}
      onClose={handleCancel}
      title={getModalTitle()}
      size="lg"
      loading={isSubmitting}
      actions={[
        {
          label: "Cancel",
          variant: "secondary",
          onClick: handleCancel,
          disabled: isSubmitting,
        },
        {
          label: "Confirm",
          variant: "primary",
          iconName: "Check",
          onClick: handleSubmit,
          inProgress: isSubmitting,
        },
      ]}
    >
      <Stack gap="md">
        {getEventTypeInfo()}

        {/* Basic Information */}
        <Grid>
          <Grid.Col span={12}>
            <TextInput
              label="Event Name"
              placeholder="Enter event name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              error={getDisplayError("name")}
              required
              withAsterisk
              size="md"
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <Textarea
              label="Description"
              placeholder="Enter event description"
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              error={getDisplayError("description")}
              rows={2}
              size="md"
            />
          </Grid.Col>
        </Grid>

        {/* Date and Time Configuration */}
        <Grid>
          <Grid.Col span={{ base: 12, lg: 4 }}>
            <DatePickerInput
              valueFormat="MMM DD, YYYY"
              label="Event Date"
              placeholder="Select event date"
              value={formData.eventDate}
              onChange={(value) => handleInputChange("eventDate", value)}
              error={getDisplayError("eventDate")}
              required
              withAsterisk
              clearable
              size="md"
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, lg: 4 }}>
            <TimeInput
              label="Start Time"
              placeholder="10:00"
              value={formData.startTime}
              onChange={(e) => handleInputChange("startTime", e.target.value)}
              error={getDisplayError("startTime")}
              required
              withAsterisk
              size="md"
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, lg: 4 }}>
            <TimeInput
              label="End Time"
              placeholder="23:00"
              value={formData.endTime}
              onChange={(e) => handleInputChange("endTime", e.target.value)}
              error={getDisplayError("endTime")}
              required
              withAsterisk
              size="md"
            />
          </Grid.Col>
        </Grid>

        {/* Location Configuration */}
        {/* {venueId && (
          <Grid>
            <Grid.Col span={12}>
              <RoomSelector
                label="Main Room"
                placeholder="Select the main room for this event (optional)"
                venueId={venueId}
                value={formData.roomId || null}
                onChange={handleMainRoomChange}
                error={getDisplayError("roomId")}
                clearable
                size="sm"
                multiple={false}
              />
            </Grid.Col>
          </Grid>
        )} */}

        {venueId && (
          <Grid>
            <Grid.Col span={12}>
              <RoomSelector
                label="Event Rooms"
                placeholder="Select rooms for this event (optional)"
                venueId={venueId}
                value={formData.rooms
                  .map((room) => room.id || "")
                  .filter((id) => !!id)}
                onChange={handleRoomsChange}
                error={getDisplayError("roomId")}
                clearable
                size="md"
                multiple={true}
              />
            </Grid.Col>
          </Grid>
        )}

        {/* Event Teams */}
        {venueId && (
          <Grid>
            <Grid.Col span={12}>
              <Title order={5} mb="sm">
                Event Teams
              </Title>
              <EventTeamSelector
                value={formData.eventTeams}
                onChange={handleEventTeamsChange}
                venueId={venueId}
                placeholder="No teams added yet. Teams help organize supporting rooms for your event."
                error={getDisplayError("eventTeams")}
                size="md"
              />
            </Grid.Col>
          </Grid>
        )}

        {/* Client Selection */}
        <Grid>
          <Grid.Col span={12}>
            <ClientSelector
              label="Client"
              placeholder="Select client for this event (optional)"
              value={formData.clientId || null}
              onChange={handleClientChange}
              error={getDisplayError("clientId")}
              clearable
              size="md"
              multiple={false}
            />
          </Grid.Col>
        </Grid>

        {/* Tags */}
        <Grid>
          <Grid.Col span={12}>
            <CategoriesInput
              label="Categories"
              placeholder="Type and press Enter to add categories"
              value={formData.categories ?? []}
              onChange={(categories) =>
                handleInputChange("categories", categories)
              }
              error={getDisplayError("categories")}
              description="Add categoroes to organize this event (e.g., 'Programs', 'Activities', etc)"
              clearable
              splitChars={[",", ";"]}
              maxTags={20}
            />
          </Grid.Col>
        </Grid>

        {/* Extetrnal URL */}
        <Grid>
          <Grid.Col span={{ base: 12, lg: 4 }}>
            <EventexternalUrlTypeSelector
              label="Type"
              value={formData.externalUrlType}
              onChange={(value) => handleInputChange("externalUrlType", value)}
              error={getDisplayError("externalUrlType")}
              required
              size="md"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 8 }}>
            <TextInput
              label="External URL"
              type="url"
              placeholder="Enter external event info URL"
              value={formData.externalUrl || ""}
              onChange={(e) => handleInputChange("externalUrl", e.target.value)}
              error={getDisplayError("externalUrl")}
              //description="Use it when you want people to RSVP or Buy tickets on external platform"
              size="md"
            />
          </Grid.Col>
        </Grid>

        {/* Status and Configuration */}
        <Grid>
          <Grid.Col span={12}>
            <Stack gap={0}>
              <InputLabel required>Status</InputLabel>
              <EventStatusSelector
                value={formData.status}
                onChange={(value) => handleInputChange("status", value)}
                error={getDisplayError("status")}
                required
                size="md"
              />
            </Stack>
          </Grid.Col>
        </Grid>

        {/* Override Information */}
        {isRecurringEventInstance && (
          <Alert
            variant="light"
            color="blue"
            title="Recurring Event Override"
            icon={<IconInfoCircle />}
          >
            <Text size="sm">
              This is an override for a recurring event and will take precedence
              over the recurring pattern for this specific date.
            </Text>
          </Alert>
        )}

        {/* Generic errors */}
        {renderGenericErrors()}
      </Stack>
    </AppModal>
  );
};
